import { DatePicker, Select } from 'antd';
import ChartComponent, { CHART_TYPES } from '../../../components/chartComponent/ChartComponent';
import { useEffect, useState, useRef } from 'react';
import FormItem from 'antd/es/form/FormItem';
import { referenceDataSyncService } from '../../../services/referenceDataSyncService';
import type { Location } from '../../../types/referenceData';
import { analysisService } from '../../../api/services/analysisService';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import dayjs from 'dayjs';
import { disableFutureDates } from '../../../utils/disableDates';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import { downloadCustomCSV } from '../../../helpers/utilityHelper';
import { useIsOnlineContext } from '../../../contexts/IsOnlineContext';
import { sanitizeInput } from '../../../utils/sanitizeInput';

interface PriceTrendOverTimeChartProps {
  t: (key: string) => string;
}

const PriceTrendOverTimeChart = ({ t }: PriceTrendOverTimeChartProps) => {
  const today = dayjs();
  // const oneYearAgo = today.subtract(1, 'year');
  const oneMonthAgo = today.subtract(1, 'month');

  const { errorNotification } = useNotificationContext();
  const [filterLoading, setFilterLoading] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [locations, setLocations] = useState<Location[]>([]);
  const [municipalities, setMunicipalities] = useState<Location[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string>();
  const [selectedMunicipalities, setSelectedMunicipalities] = useState<string[] | undefined>();
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [selectedTimeInterval, setSelectedTimeInterval] = useState<
    'daily' | 'weekly' | 'monthly' | 'yearly'
  >('daily');
  const [selectedDateRange, setSelectedDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>([
    oneMonthAgo,
    today,
  ]);

  const filtersRef = useRef({
    municipalities: [] as string[],
  });

  const [isChartdataEmpty, setIsChardataEmpty] = useState<boolean>(false);
  const [chartData, setChartData] = useState<{
    categories: string[];
    series: Array<{
      name: string;
      data: number[];
      color?: string;
    }>;
    timeInterval: string;
  }>({
    categories: [],
    series: [],
    timeInterval: 'daily',
  });
  const [locationLoading, setLocationLoading] = useState<boolean>(false);
  const [municipalityLoading, setMunicipalityLoading] = useState<boolean>(false);
  const { isOnline } = useIsOnlineContext();

  const departmentItems = Array.from(new Set(locations.map((location) => location.district))).map(
    (district) => ({
      label: district,
      value: district,
    })
  );

  const municipalityItems = municipalities.map((location) => ({
    label: location.city,
    value: location.city,
  }));

  const sideFilters = [
    { label: t('daily'), value: 'daily' },
    { label: t('weekly'), value: 'weekly' },
    { label: t('monthly'), value: 'monthly' },
    { label: t('yearly'), value: 'yearly' },
  ];

  const handleDepartmentChange = (department: string) => {
    setSelectedDepartment(department);
    setMunicipalities([]);
    setSelectedMunicipalities(undefined);

    if (department) {
      try {
        setMunicipalityLoading(true);
        const locationIdsForSelectedDepartment = locations
          ?.filter((location) => location?.district === department)
          ?.map((location) => location?.id);

        setSelectedLocationIds(locationIdsForSelectedDepartment);
        const filteredMunicipalities = locations?.filter(
          (location) => location?.district === department
        );
        setMunicipalities(filteredMunicipalities);
      } catch (error) {
        console.error('Failed to load municipalities:', error);
      } finally {
        setMunicipalityLoading(false);
      }
    } else {
      setMunicipalities([]);
      setSelectedMunicipalities(undefined);
      setSelectedLocationIds([]);
    }
  };

  const handleMunicipalityChange = (municipalityArr: any) => {
    if (municipalityArr?.length > 0 && selectedDepartment) {
      const locationIdsForSelectedMunicipalities = locations
        ?.filter(
          (location) =>
            location?.district === selectedDepartment && municipalityArr?.includes(location?.city)
        )
        ?.map((location) => location?.id);

      setSelectedLocationIds(locationIdsForSelectedMunicipalities);
    } else if (municipalityArr?.length > 0) {
      const locationIdsForSelectedMunicipality = locations
        ?.filter((location) => municipalityArr?.includes(location?.city))
        ?.map((location) => location?.id);

      setSelectedLocationIds(locationIdsForSelectedMunicipality);
    } else if (selectedDepartment) {
      const locationIdsForSelectedDepartment = locations
        ?.filter((location) => location?.district === selectedDepartment)
        ?.map((location) => location?.id);

      setSelectedLocationIds(locationIdsForSelectedDepartment);
    } else {
      setSelectedLocationIds([]);
    }

    setSelectedMunicipalities(municipalityArr);
  };

  const handleTimeIntervalChange = (filter: string | number) => {
    setSelectedTimeInterval(filter as 'daily' | 'weekly' | 'monthly' | 'yearly');
  };

  // const departmentMenuItems = {
  //   items: departmentItems,
  //   onClick: handleDepartmentItemClick,
  // };

  // const municipalityMenuItems = {
  //   items: municipalityItems,
  //   onClick: handleMunicipalityItemClick,
  // };

  const fetchChartData = async () => {
    try {
      setLoading(true);
      const params = {
        locationIds: selectedLocationIds,
        // department: selectedDepartment || undefined,
        // municipality: selectedMunicipalities || undefined,
        timeInterval: selectedTimeInterval,
        startTime: selectedDateRange && selectedDateRange[0].valueOf(),
        endTime: selectedDateRange && selectedDateRange[1].endOf('day').valueOf(),
        timezoneOffset: new Date().getTimezoneOffset(),
      };

      const data = await analysisService.getBasketAveragePrices(params);
      const dataAvailailityCheck = data && data?.series?.every((item) => item?.data?.length === 0);
      setIsChardataEmpty(dataAvailailityCheck);
      setChartData(data);
    } catch (error) {
      console.error('Failed to fetch chart data:', error);
      setIsChardataEmpty(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setFilterLoading(true);
      try {
        setLocationLoading(true);

        const locationsData = await referenceDataSyncService.getLocations();
        setLocations(locationsData as unknown as Location[]);
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLocationLoading(false);
        setFilterLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    fetchChartData();
  }, [selectedLocationIds, selectedTimeInterval, selectedDateRange]);

  const chartId = 'chartTitlePriceTrendOverTimeChart';

  const downloadPNG = async () => {
    const dataURI = await ApexCharts.exec(chartId, 'dataURI');

    const link = document.createElement('a');
    link.href = dataURI.imgURI;
    link.download = `${t(chartId)}_${Date.now()}.png`;
    link.click();

    await ApexCharts.exec(
      chartId,
      'updateOptions',
      {
        xaxis: { min: currentZoom.current.min, max: currentZoom.current.max },
        annotations: {
          texts: [],
        },
      },
      false,
      true
    );
  };

  const currentZoom = useRef({ min: null, max: null });

  return (
    <div className="chart priceTrend">
      <section className="mb-2 mt-2 flex w-full flex-col justify-start gap-4 md:mb-16 md:flex-row md:items-center md:justify-between">
        <div className="items-start">
          <h3 className="text-2xl font-semibold">{t('trend.chartTitle')}</h3>
        </div>
        {!isChartdataEmpty && (
          <div className="flex justify-end gap-3 text-sm md:gap-4">
            <PrimaryButton
              disabled={isDownloading || !isOnline}
              className="px-3"
              handleOnClick={() => {
                if (isDownloading) return;
                setIsDownloading(true);
                try {
                  downloadPNG();
                } finally {
                  setTimeout(() => setIsDownloading(false), 500);
                }
              }}
            >
              {t('downloadPNG')}
            </PrimaryButton>
            <PrimaryButton
              disabled={isDownloading || !isOnline}
              className="px-3"
              handleOnClick={() => {
                if (isDownloading) return;
                const filterData = [
                  { key: 'department', label: t('department'), value: selectedDepartment },
                  {
                    key: 'municipalities',
                    label: t('downloadFilters.municipalities'),
                    value: filtersRef.current.municipalities,
                  },
                  {
                    key: 'dateRange',
                    label: t('dateRange'),
                    value: `[${selectedDateRange?.[0]?.format('YYYY-MM-DD')},${selectedDateRange?.[1]?.format('YYYY-MM-DD')}]`,
                  },
                  {
                    key: 'timeInterval',
                    label: t('timeInterval'),
                    value: t(`${selectedTimeInterval}`),
                  },
                ];

                setIsDownloading(true);
                try {
                  downloadCustomCSV(
                    t('chartTitlePriceTrendOverTimeChart'),
                    chartData,
                    filterData,
                    t,
                    t('category')
                  );
                } finally {
                  setTimeout(() => setIsDownloading(false), 500);
                }
              }}
            >
              {t('downloadCSV')}
            </PrimaryButton>
          </div>
        )}
      </section>

      <div className="mt-4 flex flex-col items-center gap-6 lg:flex-row">
        {/* <p className='mb-2 w-full text-left lg:w-auto'>{t('filters')}:</p> */}
        <FormItem
          className="config mb-0 w-full min-w-[200px] flex-initial lg:w-1/4"
          layout="vertical"
          label={<span className="text-sm font-medium text-gray-700">{t('department')}</span>}
        >
          <Select
            value={selectedDepartment}
            options={departmentItems}
            placeholder={t('departmentPlaceholder')}
            allowClear
            onClear={() => handleDepartmentChange}
            onChange={(val) => {
              handleDepartmentChange(val);
            }}
            className="w-[144px] [&>.ant-select-selector]:!rounded-lg"
          />
        </FormItem>
        <FormItem
          className="config mb-0 w-full min-w-[200px] flex-initial lg:w-1/4"
          layout="vertical"
          label={<span className="text-sm font-medium text-gray-700">{t('municipality')}</span>}
        >
          <Select
            value={selectedMunicipalities}
            options={municipalityItems}
            placeholder={t('municipalityPlaceholder')}
            allowClear
            onClear={() => setSelectedMunicipalities(undefined)}
            showSearch
            mode="multiple"
            maxTagCount={1}
            maxTagTextLength={window.matchMedia('(min-width: 1600px)').matches ? 20 : 10}
            disabled={!selectedDepartment}
            onChange={(municipalities) => {
              filtersRef.current.municipalities = municipalities;
              handleMunicipalityChange(municipalities);
            }}
            className="w-[144px] [&>.ant-select-selector]:!rounded-lg"
          />
          {/* <Dropdown menu={municipalityMenuItems}>
            <Button
              className="w-full rounded-full"
              loading={municipalityLoading}
              disabled={!selectedDepartment}
            >
              <Space className="flex w-full justify-between text-sm font-normal">
                {selectedMunicipality || t('municipality')}
                <DownOutlined />
              </Space>
            </Button>
          </Dropdown> */}
        </FormItem>
        <FormItem
          className="relative mb-0 w-full min-w-[300px] flex-initial md:w-full lg:w-1/4"
          layout="vertical"
          label={<span className="text-sm font-medium text-gray-700">{t('dateFilter')}</span>}
        >
          <DatePicker.RangePicker
            className="mt-2 w-full rounded-lg px-4 py-2 md:mt-0"
            value={selectedDateRange}
            onChange={(dates) => setSelectedDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
            format="YYYY-MM-DD"
            placeholder={[t('from'), t('to')]}
            disabledDate={disableFutureDates}
            allowClear={false}
          />
        </FormItem>
      </div>

      <div className="flex flex-col justify-end lg:flex-row">
        <div className="mt-4 lg:mt-0">
          <Select
            popupClassName="!w-[150px]"
            className="w-[max(fit-content,120px)] [&>.ant-select-selector]:!border-none [&>.ant-select-selector]:!pl-0 [&_.ant-select-selection-item]:!text-base [&_.ant-select-selection-item]:!text-black [&_.ant-select-selection-placeholder]:!text-base [&_.ant-select-selection-placeholder]:!text-black [&_.ant-select-selection-placeholder]:hover:!text-black/50 [&_input]:!capitalize"
            options={sideFilters.map((filter) => ({
              label: filter.label,
              value: filter.value,
            }))}
            defaultValue={sideFilters[0].value}
            placeholder={t('select')}
            onChange={(value) => handleTimeIntervalChange(value)}
            value={selectedTimeInterval}
          />
        </div>
      </div>

      {isChartdataEmpty ? (
        <div key="no-data" className="mb-6 py-8 text-center text-sm text-gray-500">
          {t('noDataAvailable')}
        </div>
      ) : (
        <ChartComponent
          title={''}
          type={CHART_TYPES.LINE}
          series={chartData?.series?.map((s) => ({ ...s, name: sanitizeInput(s?.name) }))}
          options={{
            chart: {
              id: chartId,
              toolbar: {
                show: true,
                tools: {
                  download: false,
                  selection: true,
                  zoom: true,
                  zoomin: true,
                  zoomout: true,
                  pan: false,
                  reset: true,
                },
              },
              events: {
                zoomed(chart, { xaxis }) {
                  currentZoom.current = xaxis;
                },
              },
            },
            tooltip: {},
            stroke: {
              curve: 'monotoneCubic',
              width: 2,
            },
            markers: {
              size: 4,
            },
            dataLabels: {
              enabled: false,
            },
            xaxis: {
              categories: chartData?.categories?.map(sanitizeInput),
            },
            yaxis: {
              title: {
                text: t('priceYaxis'),
              },
            },
            colors: ['#00b050', '#ff0000', '#0070c0'],
            legend: {
              position: 'bottom',
              showForSingleSeries: true,
            },
            grid: {
              borderColor: '#e7e7e7',
              strokeDashArray: 4,
            },
          }}
          // sideFilters={[
          //   { label: t('daily'), value: 'daily' },
          //   { label: t('weekly'), value: 'weekly' },
          //   { label: t('monthly'), value: 'monthly' },
          //   { label: t('yearly'), value: 'yearly' }
          // ]}
          // handleSideFiltersChange={handleTimeIntervalChange}
          // selectedSideFilter={selectedTimeInterval}
          maxHeight={400}
          // topWidgets={

          // }
          loading={loading || filterLoading}
        />
      )}
    </div>
  );
};

export default PriceTrendOverTimeChart;
